import React, { useEffect, useState } from 'react';
import { apiInstanceFetch } from '../../../util/api';

// Mount in the existing Admin router; apiInstanceFetch supplies its admin JWT.
export default function SocialReports() {
  const [status, setStatus] = useState('open');
  const [rows, setRows] = useState([]);
  const [cursor, setCursor] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [selected, setSelected] = useState(null);
  const [reason, setReason] = useState('');
  async function load(next = false) {
    setBusy(true); setError('');
    try {
      const result = await apiInstanceFetch.get(`admin/social/reports?status=${status}${next && cursor ? `&cursor=${encodeURIComponent(cursor)}` : ''}`);
      setRows(previous => next ? [...previous, ...result.items.filter(row => !previous.some(old => old.id === row.id))] : result.items);
      setCursor(result.nextCursor);
    } catch (_) { setError('Unable to load reports. Please retry.'); }
    finally { setBusy(false); }
  }
  useEffect(() => { load(); /* status selects a new queue */ }, [status]); // eslint-disable-line react-hooks/exhaustive-deps
  async function review(action) {
    if (!selected || !reason.trim() || busy) return;
    if (action === 'remove' && !window.confirm('Remove this content from Howyin Connect?')) return;
    setBusy(true); setError('');
    try {
      const result = await apiInstanceFetch.patch(`admin/social/reports/${selected.id}`, { action, reason: reason.trim() });
      if (result.status === false) throw new Error();
      setSelected(null); setReason(''); await load();
    } catch (_) { setError('Review failed or another administrator already reviewed this report. Refresh and retry.'); }
    finally { setBusy(false); }
  }
  return <section style={{ padding: 24, color: '#1A1614' }}>
    <h1>Howyin Connect reports</h1>
    <label>Status <select disabled={busy} value={status} onChange={event => { setSelected(null); setStatus(event.target.value); }}>
      <option value="open">Open</option><option value="dismissed">Dismissed</option><option value="actioned">Removed</option>
    </select></label>{' '}
    <button disabled={busy} onClick={() => load()}>Refresh</button>
    {error && <p role="alert">{error}</p>}
    {busy && <p role="status">Loading…</p>}
    {!busy && !rows.length && !error && <p>No reports in this queue.</p>}
    {rows.map(row => <article key={row.id} style={{ border: '1px solid #E8E1D3', borderRadius: 12, padding: 16, marginTop: 12 }}>
      <strong>{row.reason} · {row.targetType}</strong>
      <p>Reported by {row.reporter?.name} · Author: {row.author?.name} · {new Date(row.createdAt).toLocaleString()}</p>
      <p style={{ whiteSpace: 'pre-wrap' }}>{row.evidence?.text || 'Image post'}</p>
      <p>{row.details}</p>
      <button onClick={() => { setSelected(row); setReason(''); }}>Review details and history</button>
    </article>)}
    {cursor && <button disabled={busy} onClick={() => load(true)}>Load more</button>}
    {selected && <section aria-label="Report details" style={{ border: '2px solid #D4AF37', padding: 20, marginTop: 20 }}>
      <h2>Review {selected.targetType}</h2>
      <p style={{ whiteSpace: 'pre-wrap' }}>{selected.evidence?.text}</p>
      {(selected.evidence?.mediaIds || []).map(id => <ReportImage key={id} id={id} />)}
      <h3>Review history</h3>
      {!selected.history?.length && <p>Not reviewed yet.</p>}
      {(selected.history || []).map((entry, i) => <p key={i}>{entry.action} · {entry.reason} · Reviewer {entry.reviewerId} · {new Date(entry.at).toLocaleString()}</p>)}
      {selected.status === 'open' && <>
        <label>Review reason<textarea value={reason} maxLength={500} onChange={e => setReason(e.target.value)} /></label>
        <button disabled={busy || !reason.trim()} onClick={() => review('dismiss')}>Dismiss report</button>
        <button disabled={busy || !reason.trim()} onClick={() => review('remove')}>Remove content</button>
      </>}
      <button onClick={() => setSelected(null)}>Close details</button>
    </section>}
  </section>;
}

function ReportImage({ id }) {
  // Existing JSON transport cannot fetch binary bytes. The dedicated admin
  // endpoint below returns a data URL only after JWT/report-evidence checks.
  const [src, setSrc] = useState('');
  const [error, setError] = useState(false);
  useEffect(() => {
    let current = true;
    apiInstanceFetch.get(`admin/social/media/${id}?encoding=base64`)
      .then(result => { if (current) setSrc(result.dataUrl); })
      .catch(() => { if (current) setError(true); });
    return () => { current = false; };
  }, [id]);
  if (error) return <span>Image unavailable</span>;
  return src ? <img src={src} alt="Reported attachment" style={{ width: 200, maxWidth: '100%', objectFit: 'contain', margin: 8 }} /> : <span>Loading image…</span>;
}
